import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useActor, useInterpret } from '@xstate/react';
import { Checkbox, CircularProgress, MenuItem, Select } from '@mui/material';

import { selectUser } from 'entities/user/selectors';
import history from 'utils/history';
import { getAllowedSteps } from 'components/Flow/flows/getAllowedSteps';
import { NonprofitsSelector } from 'retired/FormNonprofitsSelector/NonprofitsSelector';
import { verificationMachine } from 'components/Flow/flows/nonprofitOnboardingVerification/machine';
import { getVerificationMachineContext } from 'components/Flow/flows/nonprofitOnboardingVerification/getVerificationMachineContext';
import Organization from 'entities/organization/model';
import OrganizersApi from 'entities/organizer/api';
import { useQuery } from 'entities/useQuery';
import { UserState } from 'entities/user/reducer';
import User from 'entities/user/model';
import { Privileges } from 'utils/privileges';

const Results = ({ organizer }: { organizer: UserState }) => {
  const service = useInterpret(verificationMachine, {
    context: getVerificationMachineContext(organizer, undefined, () => ({
      privileges: [],
      nonprofit: {},
    })),
    actions: {} as any,
  });
  const [state] = useActor(service);
  const allowedSteps = organizer ? getAllowedSteps(organizer) : [];
  return (
    <>
      <h4>Onboarind steps that the user would see</h4>
      <ol>
        {allowedSteps.map((step) => (
          <li key={step}>
            <Checkbox disabled checked={organizer?.onboarding?.get(step)} />
            {step}
            {step === 'verification' ? `: ${JSON.stringify(state?.value)}` : null}
          </li>
        ))}
      </ol>
      <div>
        Completed onboarding steps: {Object.keys(organizer?.onboarding?.toJS() || {}).join(', ')}
      </div>
      <div>
        Nonprofit data validated at: {organizer?.organization?.nonprofitDataValidatedAt || '-'}
      </div>
      <h4>User data</h4>
      <pre>{JSON.stringify(organizer, null, 2)}</pre>
    </>
  );
};

const OnboardingPlaygroundPage = (): JSX.Element => {
  const user = useSelector(selectUser);
  const isAdmin = user?.type === 'Admin';

  if (!isAdmin) {
    history.replace('/');
  }

  const [nonprofit, setNonprofit] = useState<Organization | null>(null);
  const [selectedOrganizerId, setSelectedOrganizerId] = useState<string>('mock-organizer');

  const isImportedNonprofit = /^[0-9a-fA-F]{24}$/.test(nonprofit?.id || '');

  const { data: organizer, loading: organizerLoading } = useQuery(
    () => OrganizersApi.fetchPopulatedOrganizerUser(selectedOrganizerId).toPromise(),
    null,
    selectedOrganizerId,
    selectedOrganizerId === 'mock-organizer'
  );

  const { data: organizersResp, loading: organizersLoading } = useQuery(
    () => OrganizersApi.fetchRaw(nonprofit).toPromise(),
    { organizers: [], total: 0 },
    nonprofit?.id,
    !isImportedNonprofit
  );

  const mockOrganizer = nonprofit
    ? new User({
        _id: 'mock-organizer',
        id: 'mock-organizer',
        organization: nonprofit,
        email: 'Mock Organizer',
        privileges: [
          Privileges.ORGANIZATION_SELF_VERIFY_OWNERSHIP,
          Privileges.PARTNER_MANAGE,
          Privileges.ORGANIZATION_MANAGE_SETTINGS,
        ],
      })
    : null;
  const allOrganizers = [mockOrganizer, ...organizersResp.organizers];

  const populatedOrganizer = (() => {
    if (selectedOrganizerId === 'mock-organizer') {
      return mockOrganizer;
    }
    if (organizer) {
      return new User(organizer);
    }
    return null;
  })();

  return (
    <>
      <Helmet title="Onboarding Playground" />
      <h1>Onboarding Playground</h1>

      <h3>1. Select a nonprofit</h3>

      <NonprofitsSelector
        value={nonprofit ? [nonprofit] : []}
        limit={1}
        onChange={(val) => {
          setNonprofit(val[0]);
        }}
      />

      {nonprofit && (
        <>
          <h3>2. Select a user</h3>
          <Select
            value={selectedOrganizerId}
            onChange={(event) => setSelectedOrganizerId(event.target.value)}
          >
            {allOrganizers.map((theOrganizer) => (
              <MenuItem key={theOrganizer.id} value={theOrganizer.id}>
                {theOrganizer.email}
              </MenuItem>
            ))}
          </Select>
        </>
      )}

      {populatedOrganizer && <Results organizer={populatedOrganizer} />}

      {(organizersLoading || organizerLoading) && (
        <div style={{ marginTop: 20 }}>
          <CircularProgress size={24} />
        </div>
      )}
    </>
  );
};

export default OnboardingPlaygroundPage;
